import { useCallback, useEffect, useRef, useState } from 'react';

import Container from '@ui/container/container';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Text from '@ui/text/text/text';
import SettingsEditPanel from '../../controls/settings-edit-panel';
import RedirectUrlDetail from './redirect-url-detail';
import { useWorkingSession } from '@/utils/use-working-session';
import { saveWorkingProjectSessionByField } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import { validateUrl } from '@/utils/validate-url';

const RedirectUrlWidget = () => {
  const isSaving = useAppSelector(selectIsSavingWorkingProject);
  const session = useWorkingSession();
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  const customUrlEnabled = !!session?.custom_url_enabled;
  const [customUrl, setCustomUrl] = useState(session?.custom_url || '');
  const [customUrlSaveDisabled, setCustomUrlSaveDisabled] = useState(true);
  const [invalidUrlError, setInvalidUrlError] = useState('');

  const handleURLChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomUrl(e.target.value);
  }, []);

  const handleSave = useCallback(() => {
    if (!session) return;
    dispatch(saveWorkingProjectSessionByField({ uuid: session.uuid, custom_url: customUrl }));
  }, [customUrl, dispatch, session]);

  const handleCancel = () => {
    setCustomUrl(session?.custom_url || '');
  };

  useEffect(() => {
    if (inputRef.current && customUrlEnabled) {
      inputRef.current.focus();
    }
  }, [customUrlEnabled]);

  useEffect(() => {
    if (!session) return;
    setInvalidUrlError('');

    if (!customUrlEnabled) {
      setCustomUrlSaveDisabled(false);
    } else if (customUrl.includes(session.uuid)) {
      setInvalidUrlError("URL cannot point to the session you're redirecting from");
      setCustomUrlSaveDisabled(true);
    } else if (!customUrl) {
      setInvalidUrlError('');
      setCustomUrlSaveDisabled(true);
    } else if (customUrlEnabled && !validateUrl(customUrl)) {
      setInvalidUrlError('Please enter a valid URL (e.g., https://www.example.com)');
      setCustomUrlSaveDisabled(true);
    } else {
      setCustomUrlSaveDisabled(false);
    }
  }, [customUrl, customUrlEnabled, session]);

  return (
    <SettingsEditPanel
      isEditing={customUrlEnabled}
      onSave={handleSave}
      onCancel={handleCancel}
      saveDisabled={customUrlSaveDisabled}
      warningMessage={invalidUrlError || 'You will need to publish the project for this redirect to be applied.'}
      expandContent={
        <Container className="padding-h-16 flex-column gap-20">
          <Text className="P2" color="var(--global-ico-text-2)">
            All new and existing traffic will be redirected from the session page until this is disabled.
          </Text>
          <InputWithOverlay
            jsxLabel={() => (
              <Text className="L2 padding-l-16 padding-b-4" color="var(--global-ico-text-3)">
                Redirect URL
              </Text>
            )}
            inputRef={inputRef}
            inputProps={{
              placeholder: 'https://www.example.com',
              className: 'S2',
              value: customUrl,
              onChange: handleURLChange,
              disabled: isSaving,
            }}
            status={invalidUrlError !== '' ? 'error' : undefined}
          />
        </Container>
      }
    >
      <RedirectUrlDetail session={session} disabled={isSaving} />
    </SettingsEditPanel>
  );
};

export default RedirectUrlWidget;
